/* eslint-disable no-undefined */
/* eslint-disable complexity */
// import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styleSheet from './styles/scorePredictor.module.scss';
import WinnerIcon from '../../assets/icons/WinnerIcon';
import CrossIcon from '../../assets/icons/CrossIcon';

const styles = styleSheet.locals || {};

/**
 * Component for the ResultsBadge
 *
 * @param {object} props prediction option props
 * @param {number} props.correctFans number of fans who made the correct prediction
 * @param {number} props.totalFans total number of fans who made predictions
 * @param {boolean} [props.isFanCorrect] whether the fan's prediction is correct (null if no prediction made)
 * @returns {Function} <ResultsBadge />
 */
function ResultsBadge({ correctFans, totalFans, isFanCorrect }) {
    const { t } = useTranslation();

    const PERCENTAGE_MULTIPLIER = 100;

    // Returns percentage as a string with 1 decimal place unless integer
    const calculatePercentage = (number, total) => {
        if (!total) {
            return '0';
        }
        const value = (number / total) * PERCENTAGE_MULTIPLIER;
        if (Number.isInteger(value)) {
            return value.toString();
        }
        return value.toFixed(1);
    };

    const getResultText = () => {
        const correctPercentage = calculatePercentage(correctFans, totalFans);
        const incorrectPercentage = calculatePercentage(
            totalFans - correctFans,
            totalFans
        );

        // Special case: all fans were wrong
        if (correctFans === 0) {
            if (isFanCorrect === null) {
                return t('scorePredictor.resultsBadgeAllFansWrong');
            }
            return t('scorePredictor.resultsBadgeYouAndAllFansWrong');
        }

        // No prediction made
        if (isFanCorrect === null) {
            return t('scorePredictor.resultsBadgeNoVoteCorrectPercentage', {
                percentage: correctPercentage
            });
        }

        // User made a prediction
        if (isFanCorrect) {
            return t('scorePredictor.resultsBadgeYouAndCorrectPercentage', {
                percentage: correctPercentage
            });
        }

        return t('scorePredictor.resultsBadgeYouAndIncorrectPercentage', {
            percentage: incorrectPercentage
        });
    };

    // Show cross if all fans were wrong, or if the fan made a prediction and was wrong
    const shouldShowCross =
        correctFans === 0 || (isFanCorrect !== null && isFanCorrect === false);
    const text = getResultText();

    return (
        <div
            part="score-predictor-result-badge-wrapper"
            className={styles.scorePredictorResultBadgeWrapper}
            data-testid="results-badge"
        >
            <div
                part="score-predictor-result-badge"
                className={styles.scorePredictorResultBadge}
            >
                {shouldShowCross ? (
                    <span
                        className={styles.scorePredictorResultBadgeIconCross}
                        data-testid="cross-icon"
                    >
                        <CrossIcon />
                    </span>
                ) : (
                    <span
                        className={styles.scorePredictorResultBadgeIconTrophy}
                        data-testid="winner-icon"
                    >
                        <WinnerIcon />
                    </span>
                )}

                <span>{text}</span>
            </div>
        </div>
    );
}

ResultsBadge.propTypes = {
    correctFans: PropTypes.number.isRequired,
    totalFans: PropTypes.number.isRequired,
    // isFanCorrect must be either boolean or null (not undefined)
    isFanCorrect: (props, propName, componentName) => {
        const value = props[propName];
        if (value !== null && typeof value !== 'boolean') {
            return new Error(
                `Invalid prop \\"${propName}\\" supplied to \\"${componentName}\\". Must be boolean or null.`
            );
        }
        return null;
    }
};

export default ResultsBadge;
